import createClient from "openapi-fetch";
import { useSyncExternalStore } from "react";
import { config } from "../../config";
import { serviceURL } from "../../utils/service-url";

import type { paths } from "../../__definitions__/flags.changeengine.com-definitions";

const overrides =
  typeof localStorage === "undefined"
    ? {}
    : // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      (JSON.parse(localStorage.getItem("flags") || "{}") as {
        [key: string]: boolean;
      });

let features: { [key: string]: { enabled: boolean; variables: unknown } } = {};
let featuresLoading = true;
let featuresSubscriptions: (() => void)[] = [];

export const setOptimizelyUser = async (token: string): Promise<void> => {
  if (!featuresLoading) return;
  const client = createClient<paths>({
    baseUrl: serviceURL("flags"),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const response = await client.GET(`/features/{sdkKey}`, {
    params: { path: { sdkKey: config.optimizely.sdkKey } },
  });
  featuresLoading = false;
  try {
    if (response.error) throw new Error("Error fetching flags");
    features = response.data;
  } finally {
    featuresSubscriptions.forEach((l) => l());
  }
};

const getFlag = (flag: string): [boolean, unknown, boolean, boolean] =>
  flag in overrides
    ? [overrides[flag]!, {}, true, false]
    : featuresLoading
      ? [false, {}, false, false]
      : flag in features
        ? [features[flag]!.enabled, features[flag]!.variables, true, false]
        : [false, {}, true, false];

export const useFlag = (flag: string): [boolean, unknown, boolean, boolean] => {
  useSyncExternalStore(
    (listener) => {
      featuresSubscriptions = [...featuresSubscriptions, listener];
      return () => {
        featuresSubscriptions = featuresSubscriptions.filter((l) => l !== listener);
      };
    },
    () => features,
  );
  return getFlag(flag);
};

export function useFlags<T extends string>(
  ...flags: T[]
): { flags: Record<T, boolean>; flagsLoaded: boolean } {
  const flagsMap = [] as { flag: T; enabled: boolean; loaded: boolean }[];
  for (const flag of flags) {
    const [enabled, , loaded] = useFlag(flag);
    flagsMap.push({ flag, enabled, loaded });
  }

  return {
    flags: flagsMap.reduce(
      (acc, { flag, enabled }) => ({ ...acc, [flag]: enabled }),
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      {} as Record<T, boolean>,
    ),
    flagsLoaded: flagsMap.every(({ loaded }) => loaded),
  };
}

export const getFlags = (): {
  [key: string]: {
    enabled: boolean;
    source: "Optimizely" | "Override";
    variables: unknown;
  };
} =>
  Object.fromEntries(
    Object.entries(features).map(([k, v]) => [
      k,
      { ...v, source: k in overrides ? "Override" : "Optimizely" },
    ]),
  );

const ls = (): void => {
  console.table(features);
};

class FeatureFlags {
  get ls(): void {
    ls();
    return;
  }

  getFlags(): Record<string, { enabled: boolean; source: "Optimizely" | "Override" }> {
    return getFlags();
  }

  toggle(flag: string): void {
    const enabled = getFlag(flag)[0];
    overrides[flag] = !enabled;
    localStorage.setItem("flags", JSON.stringify(overrides));
    ls();
  }

  clear(): void {
    for (const flag in overrides) {
      delete overrides[flag];
    }
    localStorage.setItem("flags", "{}");
  }

  reset(flag: string): void {
    delete overrides[flag];
    localStorage.setItem("flags", JSON.stringify(overrides));
  }

  constructor() {
    if (config.Dev) {
      if (window) window.ff = this;
      console.log(`Feature Flags:
        ff.ls: List all feature flags
        ff.toggle('flag'): Toggle a feature flag
        ff.clear(): Clear all feature flag overrides
      `);
    }
  }
}

declare global {
  interface Window {
    ff: FeatureFlags;
  }
}

new FeatureFlags();
